"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useCallback } from "react";
import Link from "next/link";
import { EntityDisplay, BettingOdds } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useBetslip } from "@/features/betslip/useBetslip";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import type { TeamDisplayData } from "./TeamDisplay";

/** EntityDisplay styles for home team on desktop (name left, logo right → toward center) */
const homeTeamStylesLg = {
  imageSize: "3xl" as const,
  typography: {
    name: "text-(--background-text-color) font-semibold text-xl text-right"
  }
};

/** EntityDisplay styles for away team on desktop (logo left → toward center, name right) */
const awayTeamStylesLg = {
  imageSize: "3xl" as const,
  typography: {
    name: "text-(--background-text-color) font-semibold text-xl"
  }
};

/** EntityDisplay styles for mobile (inline, smaller) */
const teamStylesSm = {
  imageSize: "xl" as const,
  typography: {
    name: "text-(--background-text-color) font-semibold text-base"
  }
};
interface OddsItem {
  label: string;
  value: string;
  url?: string;
  providerSelectionId?: string;
  onClick?: () => void;
}
interface OperatorInfo {
  name: string;
  url: string;
  logo?: {
    url: string;
    alt: string;
  };
  backgroundColor?: string;
  textColor?: string;
}
interface PredictedOutcomeOdds {
  odds: OddsItem[];
  operator: OperatorInfo;
}
export interface PredictedViewProps {
  team: TeamDisplayData;
  opponent: TeamDisplayData;
  homeScore: string;
  awayScore: string;
  onEdit: () => void;
  oddsLoading: boolean;
  predictedOutcomeOdds: PredictedOutcomeOdds | null;
  matchId: string;
  predictorUrl: string;
  /** ISO 8601 kickoff timestamp — passed to addOddsSelection so persisted items expire 3 h after kickoff */
  kickoffAt?: string;
  labels: {
    predictionSaved: string;
    edit: string;
    viewFullGame: string;
  };
}
export const PredictedView: React.FC<PredictedViewProps> = t0 => {
  const $ = _c(77);
  const {
    team,
    opponent,
    homeScore,
    awayScore,
    onEdit,
    oddsLoading,
    predictedOutcomeOdds,
    matchId,
    predictorUrl,
    kickoffAt,
    labels
  } = t0;
  const {
    addOddsSelection,
    canAddOdds
  } = useBetslip();
  const tenantSettings = useTenantSettings();
  const shouldInterceptOdds = canAddOdds && !!matchId;
  let t1;
  if ($[0] !== addOddsSelection || $[1] !== kickoffAt || $[2] !== matchId || $[3] !== shouldInterceptOdds) {
    t1 = odd => {
      if (!shouldInterceptOdds || !matchId) {
        return;
      }
      addOddsSelection(matchId, "FT_1X2", odd.label, kickoffAt);
    };
    $[0] = addOddsSelection;
    $[1] = kickoffAt;
    $[2] = matchId;
    $[3] = shouldInterceptOdds;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleOddsClick = t1;
  let t2;
  bb0: {
    if (!shouldInterceptOdds || !predictedOutcomeOdds) {
      t2 = predictedOutcomeOdds;
      break bb0;
    }
    let t3;
    if ($[5] !== handleOddsClick || $[6] !== predictedOutcomeOdds.odds) {
      let t4;
      if ($[8] !== handleOddsClick) {
        t4 = odd_0 => ({
          ...odd_0,
          onClick: () => handleOddsClick(odd_0),
          url: undefined
        });
        $[8] = handleOddsClick;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      t3 = predictedOutcomeOdds.odds.map(t4);
      $[5] = handleOddsClick;
      $[6] = predictedOutcomeOdds.odds;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[10] !== predictedOutcomeOdds || $[11] !== t3) {
      t4 = {
        ...predictedOutcomeOdds,
        odds: t3
      };
      $[10] = predictedOutcomeOdds;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t2 = t4;
  }
  const processedOdds = t2;
  let t3;
  if ($[13] !== labels.edit || $[14] !== onEdit) {
    t3 = <div className="w-full flex sm:justify-end justify-center"><button onClick={onEdit} className="text-sm text-(--background-text-color) underline underline-offset-2 opacity-70 hover:opacity-100 transition-opacity cursor-pointer">{labels.edit}</button></div>;
    $[13] = labels.edit;
    $[14] = onEdit;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== labels.predictionSaved) {
    t4 = <span className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 text-xs sm:text-sm font-bold rounded-full bg-green-500/30 text-(--background-text-color) uppercase tracking-wider">✅ {labels.predictionSaved}</span>;
    $[16] = labels.predictionSaved;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const t5 = team.shortName || team.name;
  const t6 = team.assets?.logo;
  let t7;
  if ($[18] !== t6) {
    t7 = <TeamLogo src={t6} size="xl2" />;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t7) {
    t8 = <div className="flex items-center justify-end gap-4 min-w-0"><EntityDisplay name={t5} image={t7} imagePosition="right" styles={homeTeamStylesLg} /></div>;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const t9 = homeScore || "0";
  let t10;
  if ($[23] !== t9) {
    t10 = <div className="w-16 h-14 flex items-center justify-center bg-white rounded-lg text-gray-900 text-3xl font-bold shadow-lg">{t9}</div>;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-3xl font-black text-(--background-text-color-muted)">-</span>;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = awayScore || "0";
  let t13;
  if ($[26] !== t12) {
    t13 = <div className="w-16 h-14 flex items-center justify-center bg-white rounded-lg text-gray-900 text-3xl font-bold shadow-lg">{t12}</div>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t13) {
    t14 = <div className="flex items-center gap-2">{t10}{t11}{t13}</div>;
    $[28] = t10;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = opponent.shortName || opponent.name;
  const t16 = opponent.assets?.logo;
  let t17;
  if ($[31] !== t16) {
    t17 = <TeamLogo src={t16} size="xl2" />;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t15 || $[34] !== t17) {
    t18 = <div className="flex items-center justify-start gap-4 min-w-0"><EntityDisplay name={t15} image={t17} imagePosition="left" styles={awayTeamStylesLg} /></div>;
    $[33] = t15;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t14 || $[37] !== t18 || $[38] !== t8) {
    t19 = <div className="hidden sm:grid grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-6">{t8}{t14}{t18}</div>;
    $[36] = t14;
    $[37] = t18;
    $[38] = t8;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const t20 = team.shortName || team.name;
  const t21 = team.assets?.logo;
  let t22;
  if ($[40] !== t21) {
    t22 = <TeamLogo src={t21} size="lg" />;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t20 || $[43] !== t22) {
    t23 = <EntityDisplay name={t20} image={t22} imagePosition="left" truncate={true} styles={teamStylesSm} />;
    $[42] = t20;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  const t24 = homeScore || "0";
  let t25;
  if ($[45] !== t24) {
    t25 = <div className="w-12 h-10 flex items-center justify-center bg-white rounded-lg text-gray-900 text-2xl font-bold shadow-lg shrink-0">{t24}</div>;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t23 || $[48] !== t25) {
    t26 = <div className="flex items-center justify-between gap-3">{t23}{t25}</div>;
    $[47] = t23;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  const t27 = opponent.shortName || opponent.name;
  const t28 = opponent.assets?.logo;
  let t29;
  if ($[50] !== t28) {
    t29 = <TeamLogo src={t28} size="lg" />;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] !== t27 || $[53] !== t29) {
    t30 = <EntityDisplay name={t27} image={t29} imagePosition="left" truncate={true} styles={teamStylesSm} />;
    $[52] = t27;
    $[53] = t29;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  const t31 = awayScore || "0";
  let t32;
  if ($[55] !== t31) {
    t32 = <div className="w-12 h-10 flex items-center justify-center bg-white rounded-lg text-gray-900 text-2xl font-bold shadow-lg shrink-0">{t31}</div>;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== t30 || $[58] !== t32) {
    t33 = <div className="flex items-center justify-between gap-3">{t30}{t32}</div>;
    $[57] = t30;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t26 || $[61] !== t33) {
    t34 = <div className="sm:hidden flex flex-col gap-3 w-full">{t26}{t33}</div>;
    $[60] = t26;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== oddsLoading || $[64] !== processedOdds || $[65] !== tenantSettings.hasOdds) {
    t35 = tenantSettings.hasOdds && oddsLoading ? <div className="flex gap-2 animate-pulse w-full max-w-sm"><div className="flex-1 h-10 bg-white/10 rounded" /></div> : tenantSettings.hasOdds && processedOdds ? <div className="w-full max-w-sm"><BettingOdds odds={processedOdds.odds} operator={processedOdds.operator} /></div> : null;
    $[63] = oddsLoading;
    $[64] = processedOdds;
    $[65] = tenantSettings.hasOdds;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== labels.viewFullGame || $[68] !== predictorUrl) {
    t36 = <Link prefetch={false} href={predictorUrl} className="text-sm font-medium text-(--background-text-color) opacity-80 hover:opacity-100 transition-opacity">{labels.viewFullGame}</Link>;
    $[67] = labels.viewFullGame;
    $[68] = predictorUrl;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] !== t19 || $[71] !== t3 || $[72] !== t34 || $[73] !== t35 || $[74] !== t36 || $[75] !== t4) {
    t37 = <div className="flex flex-col items-center gap-4 sm:gap-6">{t3}{t4}{t19}{t34}{t35}{t36}</div>;
    $[70] = t19;
    $[71] = t3;
    $[72] = t34;
    $[73] = t35;
    $[74] = t36;
    $[75] = t4;
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  return t37;
};